// src/pages/sessions/components/LiveSession/hooks/useBreakoutRooms.js
import { useState, useCallback, useEffect, useRef } from 'react';
import sessionService from '@/services/sessionService';
import { useWebSocket } from '@/hooks/useWebSocket';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

// Enhanced cleanup utility for this hook
class BreakoutRoomsCleanup {
  constructor() {
    this.timeouts = new Set();
    this.handlers = new Map();
    this.unsubscribers = new Set();
    this.abortControllers = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  clearTimeout(id) {
    if (id) {
      clearTimeout(id);
      this.timeouts.delete(id);
    }
  }

  createAbortController() {
    if (this.disposed) return null;
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  addHandler(type, handler) {
    if (this.disposed) return;
    this.handlers.set(type, handler);
  }

  addUnsubscriber(unsub) {
    if (this.disposed) return;
    this.unsubscribers.add(unsub);
  }

  cleanup() {
    this.disposed = true;
    
    // Abort all fetch requests first
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {
        console.debug('Error aborting controller:', e);
      }
    });
    this.abortControllers.clear();
    
    // Clear all timeouts
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
    
    // Clear handlers reference
    this.handlers.clear();
    
    // Call all unsubscribers
    this.unsubscribers.forEach(unsub => {
      try {
        if (typeof unsub === 'function') unsub();
      } catch (e) {
        console.debug('Unsubscriber cleanup error:', e);
      }
    });
    this.unsubscribers.clear();
  }
}

export const useBreakoutRooms = (sessionId, isTeacherFn, localParticipantIdentity) => {
  const { addMessageHandler, sendMessage, removeMessageHandler } = useWebSocket();
  const { user } = useAuthStore();
  
  const [virtualRooms, setVirtualRooms] = useState([]);
  const [currentVirtualRoom, setCurrentVirtualRoom] = useState(null);
  const [participantVirtualRooms, setParticipantVirtualRooms] = useState({});
  const [teacherBroadcastRooms, setTeacherBroadcastRooms] = useState(new Set());
  const [teacherMonitorRooms, setTeacherMonitorRooms] = useState(new Set());
  const [teacherMonitorSilentRooms, setTeacherMonitorSilentRooms] = useState(new Set());
  const [isAllBroadcasting, setIsAllBroadcasting] = useState(false);
  const [isAllMonitoring, setIsAllMonitoring] = useState(false);
  const [isAllMonitoringSilent, setIsAllMonitoringSilent] = useState(false);
  
  // Store callback for force refresh
  const [forceRefreshCallback, setForceRefreshCallback] = useState(null);
  
  // CRITICAL: Store callbacks for metadata updates
  const [onBroadcastStateChange, setOnBroadcastStateChange] = useState(null);

  // Cleanup tracker
  const cleanupRef = useRef(new BreakoutRoomsCleanup());
  const cleanup = cleanupRef.current;
  
  // Refs for state that handlers need to access
  const virtualRoomsRef = useRef([]);
  const currentVirtualRoomRef = useRef(null);
  const localParticipantIdentityRef = useRef(null);
  const mountedRef = useRef(true);
  const wsConnectedRef = useRef(false);
  
  // Check if user is teacher
  const isTeacher = isTeacherFn();
  
  // Create stable teacher identity
  const teacherIdentity = isTeacher && user ? 
    `teacher_${user.id}_${user.institutionMemberId}` : null;

  // Update refs when state changes
  useEffect(() => {
    virtualRoomsRef.current = virtualRooms;
  }, [virtualRooms]);

  useEffect(() => {
    currentVirtualRoomRef.current = currentVirtualRoom;
  }, [currentVirtualRoom]);

  useEffect(() => {
    localParticipantIdentityRef.current = localParticipantIdentity;
  }, [localParticipantIdentity]);

  // CRITICAL: Notify parent when broadcast state changes
  useEffect(() => {
    if (isTeacher && onBroadcastStateChange) {
      console.log('Notifying parent of broadcast state change:', {
        broadcastingTo: Array.from(teacherBroadcastRooms),
        monitoringRooms: Array.from(teacherMonitorRooms),
        monitoringSilentRooms: Array.from(teacherMonitorSilentRooms)
      });
      onBroadcastStateChange({
        broadcastingTo: Array.from(teacherBroadcastRooms),
        monitoringRooms: Array.from(teacherMonitorRooms),
        monitoringSilentRooms: Array.from(teacherMonitorSilentRooms)
      });
    }
  }, [teacherBroadcastRooms, teacherMonitorRooms, teacherMonitorSilentRooms, isTeacher, onBroadcastStateChange]);

  // Load virtual rooms from API with abort controller
  const loadVirtualRooms = useCallback(async () => {
    if (!mountedRef.current) return [];
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return [];
    
    try {
      const response = await sessionService.getBreakoutRooms(sessionId, {
        signal: abortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return [];
      
      if (response?.success && response?.data) {
        const rooms = response.data;
        
        setVirtualRooms(rooms);
        virtualRoomsRef.current = rooms;
        
        // Build participant-room mapping
        const mapping = {};
        rooms.forEach(room => {
          if (room.participants) {
            room.participants.forEach(p => {
              mapping[p.identity] = room.id;
            });
          }
        });
        setParticipantVirtualRooms(mapping);
        
        return rooms;
      }
      return [];
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Load virtual rooms aborted');
        return [];
      }
      console.error('Error loading virtual rooms:', error);
      return [];
    }
  }, [sessionId, cleanup]);

  // Create virtual room (teacher only) with abort controller
  const handleCreateVirtualRoom = useCallback(async () => {
    if (!isTeacher || !mountedRef.current) return null;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return null;
    
    try {
      const response = await sessionService.createBreakoutRoom(sessionId, {
        signal: abortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return null;
      
      if (response?.success && response?.data) {
        const newRoom = response.data;
        
        setVirtualRooms(prev => [...prev, newRoom]);
        toast.success(`Created ${newRoom.display_name || newRoom.room_name}`);
        return newRoom;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Create room aborted');
        return null;
      }
      console.error('Error creating virtual room:', error);
      if (mountedRef.current) {
        toast.error('Failed to create breakout room');
      }
    }
    return null;
  }, [sessionId, isTeacher, cleanup]);

  // Assign participant to room with cleanup and abort controller
  const assignParticipantToRoom = useCallback(async (participantIdentity, roomId) => {
    if (!participantIdentity || !mountedRef.current) {
      console.warn('Cannot assign: no participant identity or unmounted');
      return { success: false, message: 'No participant identity' };
    }

    const abortController = cleanup.createAbortController();
    if (!abortController) return { success: false };

    try {
      const response = await sessionService.assignToVirtualRoom(
        sessionId,
        {
          participantIdentity,
          virtualRoomId: roomId
        },
        { signal: abortController.signal }
      );

      // Check mounted after async operation
      if (!mountedRef.current) return { success: false };

      if (response?.success) {
        // Update local mapping immediately
        setParticipantVirtualRooms(prev => {
          const updated = { ...prev };
          if (roomId === null || roomId === undefined) {
            delete updated[participantIdentity];
          } else {
            updated[participantIdentity] = roomId;
          }
          return updated;
        });
        
        // Force subscription refresh with tracked timeout
        cleanup.setTimeout(() => {
          if (!mountedRef.current) return;
          
          console.log('Forcing subscription refresh after room assignment');
          if (forceRefreshCallback) {
            forceRefreshCallback();
          }
          // Also try global callback as fallback
          if (window.__applySubscriptionsGlobal) {
            window.__applySubscriptionsGlobal();
          }
        }, 800);

        return response;
      }
      
      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Assign participant aborted');
        return { success: false, message: 'Request cancelled' };
      }
      console.error('Error assigning participant:', error);
      return { success: false, message: error.message };
    }
  }, [sessionId, forceRefreshCallback, cleanup]);

  // Delete virtual room with proper cleanup and abort controller
  const handleDeleteRoom = useCallback(async (roomId) => {
    if (!isTeacher || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    try {
      // First, find all participants in this room
      const participantsInRoom = Object.entries(participantVirtualRooms)
        .filter(([_, assignedRoomId]) => Number(assignedRoomId) === Number(roomId))
        .map(([participantIdentity]) => participantIdentity);
      
      // Reassign all participants to main room before deleting
      if (participantsInRoom.length > 0) {
        toast.info(`Moving ${participantsInRoom.length} participant(s) to main room...`);
        
        // Reassign each participant to main room
        for (const participantIdentity of participantsInRoom) {
          if (!mountedRef.current) return;
          await assignParticipantToRoom(participantIdentity, null);
        }
      }
      
      if (!mountedRef.current) return;
      
      // Remove from monitoring/broadcasting if teacher was monitoring/broadcasting
      if (teacherMonitorRooms.has(roomId)) {
        setTeacherMonitorRooms(prev => {
          const updated = new Set(prev);
          updated.delete(roomId);
          return updated;
        });
      }
      
      if (teacherBroadcastRooms.has(roomId)) {
        setTeacherBroadcastRooms(prev => {
          const updated = new Set(prev);
          updated.delete(roomId);
          return updated;
        });
      }
      
      // Now delete the room
      const response = await sessionService.deleteBreakoutRoom(sessionId, roomId, {
        signal: abortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      
      if (response?.success) {
        // Update local state
        setVirtualRooms(prev => prev.filter(r => r.id !== roomId));
        
        // Clean up participant mappings
        setParticipantVirtualRooms(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(key => {
            if (Number(updated[key]) === Number(roomId)) {
              delete updated[key];
            }
          });
          return updated;
        });
        
        // If current user was in this room
        if (currentVirtualRoom?.id === roomId) {
          setCurrentVirtualRoom(null);
        }
        
        // Update monitoring/broadcasting state if all rooms are deleted
        if (virtualRooms.length <= 1) {
          setIsAllBroadcasting(false);
          setIsAllMonitoring(false);
        }
        
        toast.success(participantsInRoom.length > 0 ? 
          `Room deleted and ${participantsInRoom.length} participant(s) moved to main room` : 
          'Breakout room deleted'
        );
      } else {
        throw new Error('Failed to delete room');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Delete room aborted');
        return;
      }
      console.error('Error deleting room:', error);
      if (mountedRef.current) {
        toast.error('Failed to delete breakout room');
        // Reload rooms to ensure consistency
        loadVirtualRooms();
      }
    }
  }, [sessionId, isTeacher, participantVirtualRooms, teacherMonitorRooms, teacherBroadcastRooms, 
      virtualRooms, currentVirtualRoom, assignParticipantToRoom, loadVirtualRooms, cleanup]);

  // Handle rename room with abort controller
  const handleRenameRoom = useCallback(async (roomId, newName) => {
    if (!isTeacher || !newName.trim() || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    try {
      const response = await sessionService.renameBreakoutRoom(sessionId, roomId, newName, {
        signal: abortController.signal
      });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      
      if (response?.success) {
        setVirtualRooms(prev => prev.map(r => 
          r.id === roomId ? { ...r, room_name: newName, display_name: newName } : r
        ));
        
        if (currentVirtualRoom?.id === roomId) {
          setCurrentVirtualRoom(prev => ({
            ...prev,
            room_name: newName,
            display_name: newName
          }));
        }
        
        toast.success('Room renamed successfully');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Rename room aborted');
        return;
      }
      console.error('Error renaming room:', error);
      if (mountedRef.current) {
        toast.error('Failed to rename room');
      }
    }
  }, [sessionId, isTeacher, currentVirtualRoom, cleanup]);

  const handleAssignToVirtualRoom = useCallback(async (participantIdentity, roomId) => {
    if (!mountedRef.current) return { success: false };
    
    const result = await assignParticipantToRoom(participantIdentity, roomId);
    
    if (!mountedRef.current) return result;
    
    if (result?.success) {
      const roomName = roomId ? 
        virtualRooms.find(r => r.id === roomId)?.display_name || 'breakout room' : 
        'main room';
      
      toast.success(`Participant moved to ${roomName}`);
    } else if (result?.message !== 'Request cancelled') {
      toast.error('Failed to assign participant');
    }
    
    return result;
  }, [assignParticipantToRoom, virtualRooms]);

  // Toggle teacher broadcast with abort controller - FIXED to ensure metadata update
  const toggleTeacherBroadcast = useCallback(async (roomId) => {
    if (!isTeacher || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const wasMonitoring = teacherMonitorRooms.has(roomId);
    const wasMonitoringSilent = teacherMonitorSilentRooms.has(roomId);
    const isCurrentlyBroadcasting = teacherBroadcastRooms.has(roomId);
    
    try {
      // Update state immediately for responsiveness
      if (isCurrentlyBroadcasting) {
        const newSet = new Set(teacherBroadcastRooms);
        newSet.delete(roomId);
        setTeacherBroadcastRooms(newSet);
        
        // Persist to backend
        await sessionService.updateTeacherBroadcast(sessionId, { 
          roomIds: Array.from(newSet) 
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success('Left room');
      } else {
        if (wasMonitoring) {
          setTeacherMonitorRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        if (wasMonitoringSilent) {
          setTeacherMonitorSilentRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        const newSet = new Set(teacherBroadcastRooms);
        newSet.add(roomId);
        setTeacherBroadcastRooms(newSet);
        
        // Persist to backend
        if (wasMonitoring) {
          await sessionService.updateTeacherMonitoring(sessionId, {
            roomsToMonitor: [],
            roomsToUnmonitor: [roomId]
          }, { signal: abortController.signal });
        }
        
        if (wasMonitoringSilent) {
          await sessionService.updateTeacherMonitoring(sessionId, {
            roomsToMonitor: [],
            roomsToUnmonitor: [],
            roomsToMonitorSilent: [],
            roomsToUnmonitorSilent: [roomId]
          }, { signal: abortController.signal });
        }
        
        await sessionService.updateTeacherBroadcast(sessionId, { 
          roomIds: Array.from(newSet) 
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success(wasMonitoring ? 'Switched from monitoring to joined' : wasMonitoringSilent ? 'Switched from silent monitoring to joined' : 'Joined room');
      }
      
      // CRITICAL: Force immediate subscription refresh
      cleanup.setTimeout(() => {
        if (!mountedRef.current) return;
        if (forceRefreshCallback) {
          forceRefreshCallback();
        }
        if (window.__applySubscriptionsGlobal) {
          window.__applySubscriptionsGlobal();
        }
      }, 100);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle broadcast aborted');
        return;
      }
      console.error('Error updating broadcast:', error);
      if (mountedRef.current) {
        // Revert state on error
        setTeacherBroadcastRooms(teacherBroadcastRooms);
        if (wasMonitoring) {
          setTeacherMonitorRooms(prev => {
            const updated = new Set(prev);
            updated.add(roomId);
            return updated;
          });
        }
        toast.error('Failed to update room join status');
      }
    }
  }, [sessionId, isTeacher, teacherBroadcastRooms, teacherMonitorRooms, teacherMonitorSilentRooms, cleanup, forceRefreshCallback]);

  // Toggle teacher monitor with abort controller - FIXED to ensure metadata update
  const toggleTeacherMonitor = useCallback(async (roomId) => {
    if (!isTeacher || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const wasBroadcasting = teacherBroadcastRooms.has(roomId);
    const wasMonitoringSilent = teacherMonitorSilentRooms.has(roomId);
    const isCurrentlyMonitoring = teacherMonitorRooms.has(roomId);
    
    try {
      // Update state immediately for responsiveness
      if (isCurrentlyMonitoring) {
        const newSet = new Set(teacherMonitorRooms);
        newSet.delete(roomId);
        setTeacherMonitorRooms(newSet);
        
        // Persist to backend
        await sessionService.updateTeacherMonitoring(sessionId, {
          roomsToMonitor: [],
          roomsToUnmonitor: [roomId]
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success('Stopped monitoring');
      } else {
        if (wasBroadcasting) {
          setTeacherBroadcastRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        if (wasMonitoringSilent) {
          setTeacherMonitorSilentRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        const newSet = new Set(teacherMonitorRooms);
        newSet.add(roomId);
        setTeacherMonitorRooms(newSet);
        
        // Persist to backend
        if (wasBroadcasting) {
          await sessionService.updateTeacherBroadcast(sessionId, { 
            roomIds: Array.from(teacherBroadcastRooms).filter(id => id !== roomId)
          }, { signal: abortController.signal });
        }
        
        if (wasMonitoringSilent) {
          await sessionService.updateTeacherMonitoring(sessionId, {
            roomsToMonitor: [],
            roomsToUnmonitor: [],
            roomsToMonitorSilent: [],
            roomsToUnmonitorSilent: [roomId]
          }, { signal: abortController.signal });
        }
        
        await sessionService.updateTeacherMonitoring(sessionId, {
          roomsToMonitor: [roomId],
          roomsToUnmonitor: []
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success(wasBroadcasting ? 'Switched from joined to monitoring' : wasMonitoringSilent ? 'Switched from silent monitoring to monitoring' : 'Started secret monitoring');
      }
      
      // CRITICAL: Force immediate subscription refresh
      cleanup.setTimeout(() => {
        if (!mountedRef.current) return;
        if (forceRefreshCallback) {
          forceRefreshCallback();
        }
        if (window.__applySubscriptionsGlobal) {
          window.__applySubscriptionsGlobal();
        }
      }, 100);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle monitor aborted');
        return;
      }
      console.error('Error updating monitoring:', error);
      if (mountedRef.current) {
        // Revert state on error
        setTeacherMonitorRooms(teacherMonitorRooms);
        if (wasBroadcasting) {
          setTeacherBroadcastRooms(prev => {
            const updated = new Set(prev);
            updated.add(roomId);
            return updated;
          });
        }
        toast.error('Failed to update monitoring status');
      }
    }
  }, [sessionId, isTeacher, teacherMonitorRooms, teacherBroadcastRooms, teacherMonitorSilentRooms, cleanup, forceRefreshCallback]);

  // Toggle teacher monitor silently (video only, no audio) with abort controller
  const toggleTeacherMonitorSilent = useCallback(async (roomId) => {
    if (!isTeacher || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const wasBroadcasting = teacherBroadcastRooms.has(roomId);
    const wasMonitoring = teacherMonitorRooms.has(roomId);
    const isCurrentlyMonitoringSilent = teacherMonitorSilentRooms.has(roomId);
    
    try {
      // Update state immediately for responsiveness
      if (isCurrentlyMonitoringSilent) {
        // Turn off silent monitoring
        const newSet = new Set(teacherMonitorSilentRooms);
        newSet.delete(roomId);
        setTeacherMonitorSilentRooms(newSet);
        
        // Persist to backend
        await sessionService.updateTeacherMonitoring(sessionId, {
          roomsToMonitor: [],
          roomsToUnmonitor: [],
          roomsToMonitorSilent: [],
          roomsToUnmonitorSilent: [roomId]
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success('Stopped silent monitoring');
      } else {
        // Turn on silent monitoring - must remove from broadcast and regular monitor first
        if (wasBroadcasting) {
          setTeacherBroadcastRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        if (wasMonitoring) {
          setTeacherMonitorRooms(prev => {
            const updated = new Set(prev);
            updated.delete(roomId);
            return updated;
          });
        }
        
        const newSet = new Set(teacherMonitorSilentRooms);
        newSet.add(roomId);
        setTeacherMonitorSilentRooms(newSet);
        
        // Persist to backend
        if (wasBroadcasting) {
          await sessionService.updateTeacherBroadcast(sessionId, { 
            roomIds: Array.from(teacherBroadcastRooms).filter(id => id !== roomId)
          }, { signal: abortController.signal });
        }
        
        await sessionService.updateTeacherMonitoring(sessionId, {
          roomsToMonitor: [],
          roomsToUnmonitor: wasMonitoring ? [roomId] : [],
          roomsToMonitorSilent: [roomId],
          roomsToUnmonitorSilent: []
        }, { signal: abortController.signal });
        
        if (!mountedRef.current) return;
        toast.success(
          wasBroadcasting ? 'Switched from joined to silent monitoring' : 
          wasMonitoring ? 'Switched from monitoring to silent monitoring' : 
          'Started silent monitoring (video only)'
        );
      }
      
      // CRITICAL: Force immediate subscription refresh
      cleanup.setTimeout(() => {
        if (!mountedRef.current) return;
        if (forceRefreshCallback) {
          forceRefreshCallback();
        }
        if (window.__applySubscriptionsGlobal) {
          window.__applySubscriptionsGlobal();
        }
      }, 100);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle silent monitor aborted');
        return;
      }
      console.error('Error updating silent monitoring:', error);
      if (mountedRef.current) {
        // Revert state on error
        setTeacherMonitorSilentRooms(teacherMonitorSilentRooms);
        if (wasBroadcasting) {
          setTeacherBroadcastRooms(prev => {
            const updated = new Set(prev);
            updated.add(roomId);
            return updated;
          });
        }
        if (wasMonitoring) {
          setTeacherMonitorRooms(prev => {
            const updated = new Set(prev);
            updated.add(roomId);
            return updated;
          });
        }
        toast.error('Failed to update silent monitoring status');
      }
    }
  }, [sessionId, isTeacher, teacherMonitorSilentRooms, teacherMonitorRooms, teacherBroadcastRooms, cleanup, forceRefreshCallback]);

  // Toggle broadcast all with abort controller
  const toggleBroadcastAll = useCallback(async () => {
    if (!isTeacher || virtualRooms.length === 0 || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const allRoomIds = virtualRooms.map(r => r.id);
    const newBroadcasting = !isAllBroadcasting;
    
    try {
      if (newBroadcasting) {
        setTeacherMonitorRooms(new Set());
        setIsAllMonitoring(false);
        setTeacherMonitorSilentRooms(new Set());
        setIsAllMonitoringSilent(false);
        setTeacherBroadcastRooms(new Set(allRoomIds));
      } else {
        setTeacherBroadcastRooms(new Set());
      }
      
      setIsAllBroadcasting(newBroadcasting);
      
      if (newBroadcasting && isAllMonitoring) {
        await sessionService.updateTeacherMonitoring(sessionId, {
          roomsToMonitor: [],
          roomsToUnmonitor: allRoomIds
        }, { signal: abortController.signal });
      }
      
      await sessionService.updateTeacherBroadcast(sessionId, { 
        roomIds: newBroadcasting ? allRoomIds : []
      }, { signal: abortController.signal });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      toast.success(newBroadcasting ? 
        (isAllMonitoring ? 'Switched from monitoring to joined all rooms' : 'Joined all rooms') : 
        'Left all rooms'
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle broadcast all aborted');
        return;
      }
      console.error('Error updating broadcast all:', error);
      if (mountedRef.current) {
        setIsAllBroadcasting(!newBroadcasting);
        setTeacherBroadcastRooms(teacherBroadcastRooms);
        toast.error('Failed to update room join status');
      }
    }
  }, [sessionId, isTeacher, virtualRooms, isAllBroadcasting, isAllMonitoring, teacherBroadcastRooms, cleanup]);

  // Toggle monitor all with abort controller
  const toggleMonitorAll = useCallback(async () => {
    if (!isTeacher || virtualRooms.length === 0 || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const allRoomIds = virtualRooms.map(r => r.id);
    const newMonitoring = !isAllMonitoring;
    
    try {
      if (newMonitoring) {
        setTeacherBroadcastRooms(new Set());
        setIsAllBroadcasting(false);
        setTeacherMonitorSilentRooms(new Set());
        setIsAllMonitoringSilent(false);
        setTeacherMonitorRooms(new Set(allRoomIds));
      } else {
        setTeacherMonitorRooms(new Set());
      }
      
      setIsAllMonitoring(newMonitoring);
      
      if (newMonitoring && isAllBroadcasting) {
        await sessionService.updateTeacherBroadcast(sessionId, { 
          roomIds: []
        }, { signal: abortController.signal });
      }
      
      await sessionService.updateTeacherMonitoring(sessionId, {
        roomsToMonitor: newMonitoring ? allRoomIds : [],
        roomsToUnmonitor: newMonitoring ? [] : allRoomIds
      }, { signal: abortController.signal });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      toast.success(newMonitoring ? 
        (isAllBroadcasting ? 'Switched from joined to monitoring all rooms' : 'Secret monitoring all rooms') : 
        'Stopped secret monitoring all rooms'
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle monitor all aborted');
        return;
      }
      console.error('Error updating monitor all:', error);
      if (mountedRef.current) {
        setIsAllMonitoring(!newMonitoring);
        setTeacherMonitorRooms(teacherMonitorRooms);
        toast.error('Failed to update monitoring status');
      }
    }
  }, [sessionId, isTeacher, virtualRooms, isAllMonitoring, isAllBroadcasting, teacherMonitorRooms, cleanup]);
  
  // Toggle monitor silent all with abort controller
  const toggleMonitorSilentAll = useCallback(async () => {
    if (!isTeacher || virtualRooms.length === 0 || !mountedRef.current) return;
    
    const abortController = cleanup.createAbortController();
    if (!abortController) return;
    
    const allRoomIds = virtualRooms.map(r => r.id);
    const newMonitoringSilent = !isAllMonitoringSilent;
    
    try {
      if (newMonitoringSilent) {
        // Turn on silent monitoring for all rooms - turn off broadcast and regular monitoring
        setTeacherBroadcastRooms(new Set());
        setIsAllBroadcasting(false);
        setTeacherMonitorRooms(new Set());
        setIsAllMonitoring(false);
        setTeacherMonitorSilentRooms(new Set(allRoomIds));
      } else {
        // Turn off silent monitoring
        setTeacherMonitorSilentRooms(new Set());
      }
      
      setIsAllMonitoringSilent(newMonitoringSilent);
      
      // If turning on, need to clear broadcast and monitoring first
      if (newMonitoringSilent) {
        if (isAllBroadcasting) {
          await sessionService.updateTeacherBroadcast(sessionId, { 
            roomIds: []
          }, { signal: abortController.signal });
        }
        if (isAllMonitoring) {
          await sessionService.updateTeacherMonitoring(sessionId, {
            roomsToMonitor: [],
            roomsToUnmonitor: allRoomIds
          }, { signal: abortController.signal });
        }
      }
      
      // Update silent monitoring
      await sessionService.updateTeacherMonitoring(sessionId, {
        roomsToMonitor: [],
        roomsToUnmonitor: [],
        roomsToMonitorSilent: newMonitoringSilent ? allRoomIds : [],
        roomsToUnmonitorSilent: newMonitoringSilent ? [] : allRoomIds
      }, { signal: abortController.signal });
      
      // Check mounted after async operation
      if (!mountedRef.current) return;
      toast.success(newMonitoringSilent ? 
        (isAllBroadcasting ? 'Switched from joined to silent monitoring all rooms' : 
         isAllMonitoring ? 'Switched from monitoring to silent monitoring all rooms' : 
         'Silent monitoring all rooms (video only)') : 
        'Stopped silent monitoring all rooms'
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Toggle silent monitor all aborted');
        return;
      }
      console.error('Error updating silent monitor all:', error);
      if (mountedRef.current) {
        setIsAllMonitoringSilent(!newMonitoringSilent);
        setTeacherMonitorSilentRooms(teacherMonitorSilentRooms);
        toast.error('Failed to update silent monitoring status');
      }
    }
  }, [sessionId, isTeacher, virtualRooms, isAllMonitoringSilent, isAllBroadcasting, isAllMonitoring, teacherMonitorSilentRooms, cleanup]);
  // WebSocket message handlers with proper cleanup
  useEffect(() => {
    if (!sessionId) return;
    
    wsConnectedRef.current = true;

    const handlers = {
      virtual_room_created: (data) => {
        if (!mountedRef.current) return;
        if (String(data.sessionId) === String(sessionId) && data.room) {
          setVirtualRooms(prev => {
            const exists = prev.some(r => r.id === data.room.id);
            if (!exists) {
              return [...prev, data.room];
            }
            return prev;
          });
        }
      },
      
      virtual_room_deleted: (data) => {
        if (!mountedRef.current) return;
        if (String(data.sessionId) === String(sessionId)) {
          const deletedRoomId = data.roomId;
          
          setVirtualRooms(prev => prev.filter(r => r.id !== deletedRoomId));
          setParticipantVirtualRooms(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              if (Number(updated[key]) === Number(deletedRoomId)) {
                delete updated[key];
              }
            });
            return updated;
          });
          
          if (currentVirtualRoomRef.current?.id === deletedRoomId) {
            setCurrentVirtualRoom(null);
            toast.info('Your room was closed, moved to main room');
          }
        }
      },
      
      participant_assigned: (data) => {
        if (!mountedRef.current) return;
        if (String(data.sessionId) === String(sessionId)) {
          const { participantIdentity, virtualRoomId, roomName } = data;
          
          setParticipantVirtualRooms(prev => {
            const updated = { ...prev };
            if (virtualRoomId === null || virtualRoomId === undefined) {
              delete updated[participantIdentity];
            } else {
              updated[participantIdentity] = virtualRoomId;
            }
            return updated;
          });
          
          if (participantIdentity === localParticipantIdentityRef.current && !isTeacher) {
            if (virtualRoomId === null || virtualRoomId === undefined) {
              setCurrentVirtualRoom(null);
              toast.info("You've been moved to the main room");
            } else {
              const room = virtualRoomsRef.current.find(r => r.id === virtualRoomId);
              if (room) {
                setCurrentVirtualRoom(room);
                toast.info(`You've been moved to ${room.display_name || room.room_name}`);
              }
            }
          }
        }
      },
      
      virtual_room_renamed: (data) => {
        if (!mountedRef.current) return;
        if (String(data.sessionId) === String(sessionId)) {
          const { roomId: roomIdToRename, newName } = data;
          
          setVirtualRooms(prev => prev.map(r => 
            r.id === roomIdToRename ? 
              { ...r, room_name: newName, display_name: newName } : r
          ));
          
          if (currentVirtualRoomRef.current?.id === roomIdToRename) {
            setCurrentVirtualRoom(prev => ({
              ...prev,
              room_name: newName,
              display_name: newName
            }));
          }
        }
      },
      
      all_virtual_rooms_closed: (data) => {
        if (!mountedRef.current) return;
        if (String(data.sessionId) === String(sessionId)) {
          setVirtualRooms([]);
          setParticipantVirtualRooms({});
          setCurrentVirtualRoom(null);
          setTeacherBroadcastRooms(new Set());
          setTeacherMonitorRooms(new Set());
          setTeacherMonitorSilentRooms(new Set());
          setIsAllBroadcasting(false);
          setIsAllMonitoring(false);
          toast.info('All breakout rooms closed');
        }
      }
    };

    // Store handlers in cleanup tracker
    Object.entries(handlers).forEach(([type, handler]) => {
      cleanup.addHandler(type, handler);
    });

    // Register handlers and store unsubscribers
    Object.entries(handlers).forEach(([type, handler]) => {
      const unsub = addMessageHandler(type, handler);
      cleanup.addUnsubscriber(unsub);
    });

    // Subscribe to virtual room updates
    sendMessage({ 
      type: 'subscribe_virtual_rooms',
      sessionId: parseInt(sessionId)
    });

    return () => {
      wsConnectedRef.current = false;
      
      // Unsubscribe when component unmounts
      if (mountedRef.current) {
        sendMessage({ 
          type: 'unsubscribe_virtual_rooms',
          sessionId: parseInt(sessionId)
        });
      }
    };
  }, [sessionId, addMessageHandler, sendMessage, isTeacher, cleanup]);

  // Initial load
  useEffect(() => {
    loadVirtualRooms();
  }, [loadVirtualRooms]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanup.cleanup();
    };
  }, [cleanup]);

  return {
    virtualRooms,
    currentVirtualRoom,
    participantVirtualRooms,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    teacherMonitorSilentRooms,
    isAllBroadcasting,
    isAllMonitoring,
    isAllMonitoringSilent,
    loadVirtualRooms,
    handleCreateVirtualRoom,
    handleDeleteRoom,
    handleRenameRoom,
    handleAssignToVirtualRoom,
    toggleTeacherBroadcast,
    toggleTeacherMonitor,
    toggleTeacherMonitorSilent,
    toggleBroadcastAll,
    toggleMonitorAll,
    toggleMonitorSilentAll,
    setCurrentVirtualRoom,
    setTeacherBroadcastRooms,  
    setTeacherMonitorRooms,
    setTeacherMonitorSilentRooms,
    setForceRefreshCallback,
    setOnBroadcastStateChange  // CRITICAL: Export this for parent to use
  };
};